import { useMemo } from 'react';
import { UserSchema } from '@ametista-ai/api-schema';
import { DEFAULT_AI_AGENT_NAME } from '@ametista-ai/api-schema/ai-agent';
import { useMentionMenu } from '@ametista-ai/editor/plugins/MentionPlugin/store';
import { clearFloatingTrigger } from '@ametista-ai/editor/utils';
import { useEditorEventCallback } from '@handlewithcare/react-prosemirror';
import { observer } from 'mobx-react-lite';
import { Stack } from '@mantine/core';
import { useCurrentAgency } from '@/queries/agencies/useCurrentAgency';
import { useAgencyUsers } from '@/queries/users/useAgencyUsers';
import { FloatingMenu } from '../FloatingMenu/FloatingMenu';
import { FloatingMenuItem } from '../FloatingMenu/FloatingMenuItem';

export const MentionMenu = observer(() => {
  const store = useMentionMenu();
  const { data: usersData } = useAgencyUsers();
  const users = useMemo(() => usersData?.data ?? [], [usersData]);
  const { data: agency } = useCurrentAgency();

  const handleAddUser = useEditorEventCallback((view, user: UserSchema) => {
    clearFloatingTrigger(view, store.query, '@');

    const mentionNode = view.state.schema.nodes['mention'].create(
      {
        modelId: user.id,
      },
      view.state.schema.text(user.displayName)
    );

    view.dispatch(view.state.tr.insert(view.state.selection.from, mentionNode).insertText(' '));
    view.focus();

    store.setOpen(false);
  });

  const handleAddAi = useEditorEventCallback((view) => {
    clearFloatingTrigger(view, store.query, '@');

    const mentionNode = view.state.schema.nodes['mention'].create(
      {
        modelId: 'ai',
      },
      view.state.schema.text(agency?.aiAgentName ?? DEFAULT_AI_AGENT_NAME)
    );

    view.dispatch(view.state.tr.insert(view.state.selection.from, mentionNode));
    view.focus();

    store.setOpen(false);
  });

  const options = useMemo(
    () =>
      users
        .filter(
          (user) =>
            user.status === 'accepted' &&
            user.role !== 'system' &&
            !user.archived &&
            !user.email.includes('trello-') &&
            (!store.query ||
              user.displayName.toLowerCase().includes(store.query.toLowerCase()) ||
              user.email.toLowerCase().includes(store.query.toLowerCase()))
        )
        .map((user) => ({
          key: user.id,
          label: user.displayName,
          onClick: () => {
            handleAddUser(user);
          },
        })),
    [users, handleAddUser, store.query]
  );

  if (!store.open) return null;

  return (
    <FloatingMenu onOpenChange={(opened) => store.setOpen(opened)}>
      <Stack gap={0}>
        <FloatingMenuItem key="ai-instructions" itemIndex={0} onClick={handleAddAi}>
          {agency?.aiAgentName ?? 'AI'}
        </FloatingMenuItem>
        {options.map((option, index) => (
          <FloatingMenuItem key={option.key} itemIndex={index + 1} onClick={option.onClick}>
            {option.label}
          </FloatingMenuItem>
        ))}
      </Stack>
    </FloatingMenu>
  );
});
